/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import useProfileSizes from '~/pdp/utils/useProfileSizes';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Link } from '~/techstyle-shared/next-routes';
import { mobile } from '~/techstyle-shared/react-components';
import { ProductType } from '~/techstyle-shared/react-products';

import { MAX_CARD_WIDTH } from '../../constants';
import { useGridFacets } from '../../context/GridDataContexts';
import useGridInputs from '../../hooks/useGridInputs';
import useProductCardTracking from '../../hooks/useProductCardTracking';
import {
  getActiveProduct,
  getSwatchesWithPdpLink,
  shouldShowPlusSizeImageForProductCard,
} from '../../utils/activeProduct';

import ProductCardCalloutOverlay from './ProductCardCalloutOverlay';
import ProductCardFavoriteButton from './ProductCardFavoriteButton';
import ProductCardImageBadge from './ProductCardImageBadge';
import ProductCardImageFromImageService from './ProductCardImageFromImageService';
import ProductCardImageLockedTreatmentWrapper from './ProductCardImageLockedTreatmentWrapper';
import ProductCardInfoIndicator from './ProductCardInfoIndicator';
import ProductCardPricing from './ProductCardPricing';
import ProductCardSwatches from './ProductCardSwatches';
import ProductName from './ProductName';

const TrackedLink = styled.a`
  display: block;
  width: 100%;
  position: relative;
`;

const ProductCardContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${MAX_CARD_WIDTH}px;
`;

const ProductCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  gap: 12px;
  align-items: flex-start;
  align-self: stretch;
  margin-bottom: 24px;
  ${mobile`
    gap: 8px;
  `}
`;

const TagAndProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px; /* desktop */
  align-self: stretch;
  ${mobile`
    gap: 12px;
  `}
`;

const BottomCardContent = styled.div`
  display: flex;
  padding: 0 16px; /* desktop */
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  ${mobile`
    padding: 0 12px;
  `}
`;

const ProductCard = ({
  relatedProductsData,
  facets,
  id,
  isFirstProductCard,
  gridIndex,
  onActiveProductChange,
  ...rest
}) => {
  const [activeProductId, setActiveProductId] = useState(id);

  // Notify the virtualized grid when the active product changes (e.g. swatch
  // click adds promo labels and the card grows). Without this, the row keeps
  // its stale cached height until the user scrolls. Skip the first run by
  // tracking the previous id — re-firing on parent re-renders that change
  // `onActiveProductChange` identity is harmless because the ref check
  // short-circuits.
  const previousActiveProductIdRef = useRef(activeProductId);
  useLayoutEffect(() => {
    if (previousActiveProductIdRef.current === activeProductId) {
      return;
    }
    previousActiveProductIdRef.current = activeProductId;
    if (onActiveProductChange) {
      const matchedRelatedProduct = relatedProductsData?.products?.find(
        (product) => product?.masterProductId === activeProductId
      );
      onActiveProductChange(matchedRelatedProduct);
    }
  }, [activeProductId, onActiveProductChange, relatedProductsData]);
  const { productSource, gridType } = useGridInputs();
  const { selectedUrlFilters } = useGridFacets();
  const originalColor = useRef();
  const secondarySite = useSecondarySite();

  const { profileSizes } = useProfileSizes({ isPdp: false });
  const showPlusSizeImage = shouldShowPlusSizeImageForProductCard({
    productFacets: facets,
    selectedUrlFilters,
    profileSizes,
    gridType,
  });

  const relatedProducts = useMemo(
    () => relatedProductsData?.products || [],
    [relatedProductsData]
  );
  const totalProducts = relatedProductsData?.total || 0;
  const swatches = useMemo(
    () =>
      relatedProductsData?.products?.map((product) => ({
        ...product?.swatch,
        isOutOfStock:
          // master for normal products
          product?.availableQuantity?.master === 0 &&
          // anyProfile for outfits... though they don't use swatches... :think:
          product?.availableQuantity?.anyProfile === 0,
      })) || [],
    [relatedProductsData]
  );

  // primaryProductData identity changes every render due to ...rest spread; see each hook comment
  // eslint-disable-next-line react-hooks/exhaustive-deps -- object spread creates new identity each render
  const primaryProductData = { id, ...rest };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- primaryProductData changes every render due to ...rest spread
  const activeProduct = useMemo(() => {
    const activeProductData = getActiveProduct({
      relatedProducts,
      swatches,
      primaryProductData,
      productSource,
      activeProductId,
      gridType,
    });

    if (!originalColor.current) {
      originalColor.current = activeProductData?.swatchLabel;
    }

    return activeProductData;
  }, [
    relatedProducts,
    swatches,
    primaryProductData,
    productSource,
    activeProductId,
    gridType,
  ]);

  const pdpLink = secondarySite
    ? `/${secondarySite}${activeProduct?.pdpLink}`
    : activeProduct?.pdpLink;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- primaryProductData changes every render due to ...rest spread
  const swatchesWithPdpLink = useMemo(
    () =>
      getSwatchesWithPdpLink({
        swatches,
        productSource,
        primaryProductData,
        relatedProducts,
        gridType,
      }),
    [swatches, productSource, primaryProductData, relatedProducts, gridType]
  );

  const {
    trackProductClick,
    productCardRef,
    trackingTags,
    trackProductSwatchClick: baseTrackProductSwatchClick,
  } = useProductCardTracking({
    activeProduct,
    productSource,
    gridIndex,
  });

  // Bundles do not have swatch images. The swatch urls may exist from the APIs (or are manually constructed),
  // but the image binary is not available when trying to fetch from the CDN.
  // If the primary product is a bundle, we do not show the swatches, even for related products
  const showProductCardSwatches =
    activeProduct?.productTypeId !== ProductType.BUNDLE && !secondarySite;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- primaryProductData changes every render due to ...rest spread
  const trackProductSwatchClick = useCallback(
    (swatch) => {
      const {
        permalink,
        swatchLabel,
        masterProductId,
        itemNumber,
        dateExpected,
        tagIdList,
      } = getActiveProduct({
        primaryProductData,
        relatedProducts: relatedProductsData?.relatedProducts,
        activeProductId: swatch?.productId,
        productSource,
        gridType,
      });
      const { url: swatchUrl } = swatch;

      const trackingData = {
        permalink,
        swatchUrl,
        masterProductId,
        itemNumber,
        dateExpected,
        tagIdList,
        color: swatchLabel,
        originalColor: originalColor.current,
      };

      baseTrackProductSwatchClick(trackingData);
    },
    [
      primaryProductData,
      relatedProductsData,
      productSource,
      gridType,
      baseTrackProductSwatchClick,
    ]
  );

  return (
    <ProductCardContainer ref={productCardRef} {...trackingTags}>
      <ProductCardImageBadge product={activeProduct} />
      <ProductCardFavoriteButton product={activeProduct} />
      <Link href={pdpLink}>
        <TrackedLink onClick={trackProductClick}>
          <ProductCardImageLockedTreatmentWrapper
            tagIdList={activeProduct?.tagIdList}
          >
            <ProductCardImageFromImageService
              imageServiceUrl={activeProduct?.imageServiceUrl}
              imagesSummary={activeProduct?.imagesSummary}
              isFirstProductCard={isFirstProductCard}
              swatchLabel={activeProduct?.swatchLabel}
              showPlusSizeImage={showPlusSizeImage}
            />
          </ProductCardImageLockedTreatmentWrapper>
          <ProductCardCalloutOverlay product={activeProduct} />
        </TrackedLink>
      </Link>
      <ProductCellWrapper>
        {showProductCardSwatches && (
          <ProductCardSwatches
            swatches={swatchesWithPdpLink}
            activeProductId={activeProductId}
            setActiveProductId={setActiveProductId}
            activeProductPdpLink={pdpLink}
            trackProductSwatchClick={trackProductSwatchClick}
            totalProducts={totalProducts}
          />
        )}
        <Link href={pdpLink}>
          <TrackedLink onClick={trackProductClick}>
            <TagAndProductDetails>
              <ProductCardInfoIndicator product={activeProduct} />
              <BottomCardContent>
                <ProductName>
                  {activeProduct?.label.replace(/&amp;/g, '&')}
                </ProductName>
                <ProductCardPricing product={activeProduct} />
              </BottomCardContent>
            </TagAndProductDetails>
          </TrackedLink>
        </Link>
      </ProductCellWrapper>
    </ProductCardContainer>
  );
};

export default ProductCard;
